package com.far.suller.components

import androidx.compose.runtime.*
import com.far.suller.models.Section
import com.far.suller.styles.LogoStyle
import com.far.suller.styles.NavigationItemStyle
import com.far.suller.util.Constants.FONT_FAMILY
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.FaBars
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*

@OptIn(ExperimentalComposeWebApi::class)
object HeaderStyle : StyleSheet() {
    val slideDown by keyframes {
        from {
            opacity(0)
            transform { translateY((-20).px) }
        }
        to {
            opacity(1)
            transform { translateY(0.px) }
        }
    }

    val blink by keyframes {
        from { opacity(1) }
        to { opacity(0) }
    }
}

val HeaderContainerStyle = CssStyle {
    base {
        Modifier.fillMaxWidth(100.percent)
            .height(80.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.01f))
            .borderRadius(50.px)
            .padding(leftRight = 0.px)
            .transition(
                Transition.of("all", 400.ms),
                Transition.of("background-color", 400.ms),
                Transition.of("width", 400.ms),
                Transition.of("height", 400.ms),
                Transition.of("padding", 400.ms),
                Transition.of("box-shadow", 400.ms),
            )
    }
}

val ScrolledHeaderStyle = CssStyle {
    base {
        Modifier.fillMaxWidth(90.percent)
            .height(70.px)
            .backgroundColor(Colors.White)
            .padding(leftRight = 40.px)
            .boxShadow(
                offsetX = 0.px,
                offsetY = 10.px,
                blurRadius = 30.px,
                color = Color.rgba(0, 0, 0, 0.15f),
            )
    }
}

@OptIn(ExperimentalComposeWebApi::class)
@Composable
fun header(onMenuClicked: () -> Unit) {
    Style(HeaderStyle)
    val breakpoint = rememberBreakpoint()
    var scrolled by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        // Initial check
        scrolled = window.pageYOffset > 20.0
        // Robust scroll listener
        val listener = { _: dynamic -> scrolled = window.pageYOffset > 20.0 }
        window.addEventListener("scroll", listener)
    }

    Column(
        modifier =
        Modifier.fillMaxWidth()
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .zIndex(100)
            .padding(
                top = 20.px,
                leftRight = 5.percent,
            ),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Main Navigation Capsule
        Row(
            modifier =
            HeaderContainerStyle.toModifier()
                .then(if (scrolled) ScrolledHeaderStyle.toModifier() else Modifier),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            leftSide(breakpoint = breakpoint, isDark = scrolled, onMenuClicked = onMenuClicked)

            if (breakpoint > Breakpoint.MD) {
                // Nav Items
                Row(
                    modifier =
                    Modifier.then(
                        if (!scrolled) {
                            Modifier.borderRadius(50.px)
                                .backgroundColor(
                                    Color.rgba(255, 255, 255, 0.05f),
                                )
                                .padding(
                                    leftRight = 24.px,
                                    topBottom = 12.px,
                                )
                                .border(
                                    1.px,
                                    LineStyle.Solid,
                                    Color.rgba(255, 255, 255, 0.05f),
                                )
                        } else {
                            Modifier
                        },
                    )
                        .transition(Transition.of("all", 400.ms)),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Section.entries.toTypedArray().take(8).forEach { section ->
                        Link(
                            modifier =
                            NavigationItemStyle.toModifier()
                                .margin(leftRight = 12.px)
                                .fontFamily(FONT_FAMILY)
                                .fontSize(14.px)
                                .fontWeight(
                                    if (scrolled) {
                                        FontWeight.SemiBold
                                    } else {
                                        FontWeight.Medium
                                    },
                                )
                                .textDecorationLine(TextDecorationLine.None)
                                .color(
                                    if (scrolled) {
                                        Color.rgb(44, 62, 80)
                                    } else {
                                        Colors.White
                                    },
                                )
                                .transition(Transition.of("color", 400.ms)),
                            path = section.path,
                            text = section.title,
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun leftSide(breakpoint: Breakpoint, isDark: Boolean, onMenuClicked: () -> Unit) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        if (breakpoint <= Breakpoint.MD) {
            FaBars(
                modifier =
                Modifier.margin(right = 15.px)
                    .color(if (isDark) Color.rgb(44, 62, 80) else Colors.White)
                    .onClick {
                        onMenuClicked()
                    },
                size = IconSize.XL,
            )
        }

        Logo(modifier = LogoStyle.toModifier(), isDark = isDark)
    }
}
